import { LegalPage } from "@/pages/legal/LegalPage";

const EFFECTIVE = "August 12, 2026";
const UPDATED = "August 12, 2026";

export default function PrivacyPolicy() {
  return (
    <LegalPage
      testid="legal-privacy-page"
      title="Privacy Policy"
      effectiveDate={EFFECTIVE}
      lastUpdated={UPDATED}
    >
      <p>
        This Privacy Policy describes how the <strong>Family Chapel Staff
        Dashboard</strong> (&ldquo;Staff Dashboard&rdquo;, &ldquo;we&rdquo;,
        &ldquo;our&rdquo;) handles information. The Staff Dashboard is an
        internal application operated by Family Chapel and made available
        only to authorized Family Chapel personnel.
      </p>

      <h2>About the Staff Dashboard</h2>
      <p>
        The Staff Dashboard is an internal tool used by authorized Family
        Chapel staff to view and manage church operations. It is not a
        public product and is not offered to individuals outside of Family
        Chapel.
      </p>

      <h2>Information the Staff Dashboard May Handle</h2>
      <p>
        Depending on the features an authorized user accesses, the Staff
        Dashboard may contain or process:
      </p>
      <ul>
        <li>Church operational information (announcements, calendars, events, and internal notes).</li>
        <li>Staff information (names, roles, work contact details, and permission levels).</li>
        <li>Ministry information (ministry activity, participation, KPI reporting, and program summaries).</li>
        <li>Financial and accounting information available through authorized integrations, used for internal reporting and dashboards.</li>
        <li>Documents and administrative records uploaded or generated within the Staff Dashboard for internal use.</li>
        <li>Basic technical information such as sign-in activity, user role, and audit-log entries necessary to operate the application securely.</li>
      </ul>

      <h2>Third-Party Integrations</h2>
      <p>
        The Staff Dashboard integrates with third-party services as
        authorized by Family Chapel. These may include, among others,
        Google Workspace (Calendar, Drive), Pushpay (giving and church
        management), Mailchimp, and <strong>Intuit QuickBooks Online</strong>.
      </p>
      <p>
        Where the Staff Dashboard connects to a third-party service, it
        does so using credentials or access tokens that Family Chapel
        supplies during setup. Those credentials are stored on the server
        and are not shown to ordinary users of the Staff Dashboard.
      </p>

      <h2>QuickBooks Online Integration</h2>
      <p>
        With Family Chapel&rsquo;s authorization, the Staff Dashboard
        connects to QuickBooks Online to display internal financial
        reporting and dashboards for church leadership.
      </p>
      <ul>
        <li>
          The Staff Dashboard accesses the financial and accounting
          information reasonably necessary to produce these internal
          reports (for example, income and expense totals, chart-of-accounts
          data, class-level ministry rollups, and budget data).
        </li>
        <li>
          The QuickBooks integration is <strong>read-only</strong>. The
          Staff Dashboard does not create, modify, or delete any records,
          accounts, transactions, budgets, or other data inside
          QuickBooks Online.
        </li>
        <li>
          QuickBooks credentials and access tokens are securely stored on
          the server. They are not displayed to ordinary users of the
          Staff Dashboard.
        </li>
        <li>
          Information obtained from QuickBooks Online is used solely for
          Family Chapel&rsquo;s legitimate internal financial reporting
          and administrative purposes.
        </li>
      </ul>

      <h2>How the Information Is Used</h2>
      <p>
        Information available in or obtained through the Staff Dashboard is
        used only for legitimate Family Chapel operational purposes,
        including internal reporting, ministry planning, staff
        coordination, communications, and administration.
      </p>

      <h2>Information Sharing</h2>
      <p>
        Family Chapel does not sell personal or financial information.
        Access to information in the Staff Dashboard is limited to
        authorized Family Chapel personnel. Information may be shared with
        service providers that support the operation of the Staff
        Dashboard (such as the hosting platform and the integrated
        third-party services listed above) as necessary for those services
        to function.
      </p>

      <h2>Access Controls and Safeguards</h2>
      <p>
        Access to the Staff Dashboard is restricted to authorized users
        who sign in with an approved account. User roles determine what
        each user can see and do. Family Chapel applies reasonable
        administrative and technical safeguards designed to protect
        information in the Staff Dashboard, including access restrictions,
        server-side storage of integration credentials, and internal audit
        logging. No system can guarantee complete security, and users are
        expected to protect their own credentials.
      </p>

      <h2>Third-Party Practices</h2>
      <p>
        Third-party services that the Staff Dashboard integrates with are
        operated by their respective providers and are also governed by
        those providers&rsquo; own privacy, security, and terms
        documentation. Family Chapel does not control those providers.
      </p>

      <h2>Contact</h2>
      <p>
        For questions about this Privacy Policy or the Staff Dashboard,
        please contact Family Chapel through the church&rsquo;s normal
        administrative channels.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        Family Chapel may update this Privacy Policy from time to time.
        The &ldquo;Last updated&rdquo; date at the top of this page will
        reflect the most recent revision.
      </p>
    </LegalPage>
  );
}
